import { extendTheme } from "@mui/joy/styles";
import { placeholderStyles } from "../shared/inputStyles";

// SSI Strategy Brand Colors
// Based on SSI Strategy Primary Palette branding guide
const ssiColors = {
  // Primary: Ocean Blue (#009CCF) - Bright, energetic (matches SSI website buttons)
  primary: {
    50: "#e6f7fc",
    100: "#b3e8f7",
    200: "#80d9f2",
    300: "#4dcaed",
    400: "#26bbe8",
    500: "#009ccf", // SSI Brand: Ocean Blue (default button color)
    600: "#0081ac",
    700: "#006789", // Darker for hover states
    800: "#004c66",
    900: "#003243",
  },
  // Secondary: Midnight Sky Blue (#003C71) - Deep, professional accent
  secondary: {
    50: "#e6f0f7",
    100: "#b3d4e8",
    200: "#80b8d9",
    300: "#4d9bca",
    400: "#2684bc",
    500: "#006dad",
    600: "#005a91",
    700: "#003c71", // SSI Brand: Midnight Sky Blue (used for hover states)
    800: "#002d55",
    900: "#001e39",
  },
  // Neutral: Stone Grey (#717B87) with Sand Beige influence
  neutral: {
    50: "#f7f8f9",
    100: "#e8eaec",
    200: "#d6d9dc",
    300: "#c4c8cd",
    400: "#9ea4ab",
    500: "#717b87", // SSI Brand: Stone Grey
    600: "#5d6570",
    700: "#4a5159",
    800: "#373d42",
    900: "#24282b",
  },
  // Success: Northern Lights Turquoise (#6EBCB0)
  success: {
    50: "#eef9f7",
    100: "#d0efe9",
    200: "#b2e5dc",
    300: "#94dbce",
    400: "#81d1c1",
    500: "#6ebcb0", // SSI Brand: Northern Lights Turquoise
    600: "#5aa094",
    700: "#468478",
    800: "#32685c",
    900: "#1e4c40",
  },
  // Warning: Sunrise Sky Yellow (#F2A900)
  warning: {
    50: "#fef6e6",
    100: "#fde7b3",
    200: "#fbd880",
    300: "#fac94d",
    400: "#f9be26",
    500: "#f2a900", // SSI Brand: Sunrise Sky Yellow
    600: "#ca8d00",
    700: "#a27100",
    800: "#7a5500",
    900: "#523900",
  },
  // Danger: Using a red that complements the SSI palette
  danger: {
    50: "#fef2f2",
    100: "#fee2e2",
    200: "#fecaca",
    300: "#fca5a5",
    400: "#f87171",
    500: "#ef4444",
    600: "#dc2626",
    700: "#b91c1c",
    800: "#991b1b",
    900: "#7f1d1d",
  },
};

// Create the Joy UI theme with SSI brand colors
export const ssiTheme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: ssiColors.primary,
        neutral: ssiColors.neutral,
        success: ssiColors.success,
        warning: ssiColors.warning,
        danger: ssiColors.danger,
        background: {
          body: "#f5f5f5",
          surface: "#fafaf9", // Subtle warm tone influenced by Sand Beige
          level1: "#ffffff",
          level2: "#f7f7f6", // Hint of Sand Beige warmth
          level3: "#e8eaec",
        },
        text: {
          primary: "#000000", // SSI Brand: Pitch Black
          secondary: "#4a5159",
          tertiary: "#717b87", // SSI Brand: Stone Grey
        },
        divider: "#d6d9dc",
        focusVisible: "var(--joy-palette-primary-500)",
      },
    },
    // Dark mode inherits from default theme (as requested)
    dark: {
      palette: {
        primary: {
          50: "#fffbf0",
          100: "#fef3c7",
          200: "#fde68a",
          300: "#fcd34d",
          400: "#fbbf24",
          500: "#f59e0b",
          600: "#d97706",
          700: "#b45309",
          800: "#92400e",
          900: "#78350f",
        },
        neutral: {
          50: "#f8fafc",
          100: "#f1f5f9",
          200: "#e2e8f0",
          300: "#cbd5e1",
          400: "#94a3b8",
          500: "#64748b",
          600: "#475569",
          700: "#334155",
          800: "#1e293b",
          900: "#0f172a",
        },
        success: {
          50: "#f0fdf4",
          100: "#dcfce7",
          200: "#bbf7d0",
          300: "#86efac",
          400: "#4ade80",
          500: "#22c55e",
          600: "#16a34a",
          700: "#15803d",
          800: "#166534",
          900: "#14532d",
        },
        warning: {
          50: "#fffbeb",
          100: "#fef3c7",
          200: "#fde68a",
          300: "#fcd34d",
          400: "#fbbf24",
          500: "#f59e0b",
          600: "#d97706",
          700: "#b45309",
          800: "#92400e",
          900: "#78350f",
        },
        danger: {
          50: "#fef2f2",
          100: "#fee2e2",
          200: "#fecaca",
          300: "#fca5a5",
          400: "#f87171",
          500: "#ef4444",
          600: "#dc2626",
          700: "#b91c1c",
          800: "#991b1b",
          900: "#7f1d1d",
        },
        background: {
          body: "#000000",
          surface: "#181820",
          level1: "#334155",
          level2: "#475569",
          level3: "#64748b",
        },
        text: {
          primary: "#f8fafc",
          secondary: "#cbd5e1",
          tertiary: "#94a3b8",
        },
        divider: "#303040",
        focusVisible: "var(--joy-palette-primary-500)",
      },
    },
  },
  // Typography improvements
  fontFamily: {
    display: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    body: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  fontSize: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    xl2: "1.5rem",
    xl3: "1.875rem",
    xl4: "2.25rem",
  },
  // Typography styling for headers
  typography: {
    h1: {
      color: "var(--joy-palette-primary-500)", // SSI Ocean Blue color matching buttons/sliders/checkboxes
      fontWeight: 400,
    },
    h2: {
      color: "var(--joy-palette-primary-500)", // SSI Ocean Blue color matching buttons/sliders/checkboxes
      fontWeight: 400,
    },
    h3: {
      color: "var(--joy-palette-primary-500)", // SSI Ocean Blue color matching buttons/sliders/checkboxes
      fontWeight: 400,
    },
    h4: {
      color: "var(--joy-palette-primary-500)", // SSI Ocean Blue color matching buttons/sliders/checkboxes
      fontWeight: 400,
    },
    "title-lg": {
      fontWeight: 400,
    },
    "title-md": {
      fontWeight: 400,
    },
    "title-sm": {
      fontWeight: 400,
    },
  },
  // Custom component tokens for consistent styling
  components: {
    JoyButton: {
      styleOverrides: {
        root: {
          fontWeight: 400,
          borderRadius: "8px",
          minHeight: "40px",
          padding: "8px 16px",
          minWidth: "100px",
          wordWrap: "break-word",
          whiteSpace: "normal",
        },
      },
    },
    JoyInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          color: "var(--joy-palette-text-primary)",
        },
        input: {
          // Use shared placeholder styles on the actual input element
          ...placeholderStyles,
        },
      },
    },
    JoyTextarea: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          color: "var(--joy-palette-text-primary)",
        },
        textarea: {
          // Use shared placeholder styles on the actual textarea element
          ...placeholderStyles,
        },
      },
    },
    JoySelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          color: "var(--joy-palette-text-primary)",
        },
      },
    },
    JoyCard: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--joy-palette-background-surface)",
          border: "1px solid",
          borderColor: "var(--joy-palette-divider)",
          borderRadius: "12px",
          color: "var(--joy-palette-text-primary)",
        },
      },
    },
    JoyTable: {
      styleOverrides: {
        root: {
          color: "var(--joy-palette-text-primary)",
        },
      },
    },
    JoyModal: {
      styleOverrides: {
        backdrop: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        root: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      },
    },
    JoyChip: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          fontWeight: 500,
        },
      },
    },
    JoyBadge: {
      styleOverrides: {
        badge: {
          borderRadius: "10px",
          fontWeight: 600,
        },
      },
    },
    JoyTooltip: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          backgroundColor: "var(--joy-palette-background-level1)",
          color: "var(--joy-palette-text-primary)",
          border: "1px solid var(--joy-palette-divider)",
        },
      },
    },
    JoyMenu: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          border: "1px solid var(--joy-palette-divider)",
          backgroundColor: "var(--joy-palette-background-surface)",
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    JoyCircularProgress: {
      defaultProps: {
        thickness: 3,
      },
    },
    JoyAccordion: {
      styleOverrides: {
        root: {
          transition: "all 0.2s ease",
          overflow: "hidden",
        },
      },
    },
    JoyAccordionDetails: {
      styleOverrides: {
        root: {
          paddingLeft: "8px",
        },
      },
    },
    JoyAccordionSummary: {
      styleOverrides: {
        button: {
          transition: "all 0.2s ease",
        },
      },
    },
    JoySlider: {
      styleOverrides: {
        root: {
          "--Slider-thumbSize": "12px",
          "--Slider-thumbWidth": "12px",
        },
      },
    },
  },
});
